import { useCallback, useEffect, useState } from "react";

import actionableInsightsService, {
  ActionableInsight,
} from "@/services/actionable-insights.service";

const LAB_HEALTH_SUMMARY_TYPE = "LAB_HEALTH_SUMMARY";

const pickLatest = (
  insights: ActionableInsight[]
): ActionableInsight | null => {
  if (!insights || insights.length === 0) return null;
  return [...insights].sort((a, b) => {
    const aDate = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
    const bDate = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
    return bDate - aDate;
  })[0];
};

/**
 * Fetches the most recent LAB_HEALTH_SUMMARY actionable insight for a member.
 * The `summary` field is the deep-analysis narrative shown on the Lab Insights
 * landing screen as the Interpretation card.
 */
export function useLabHealthSummary(memberId: string | null | undefined) {
  const [insight, setInsight] = useState<ActionableInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchSummary = useCallback(async () => {
    if (!memberId) {
      setInsight(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await actionableInsightsService.getHealthSummary(
        memberId,
        LAB_HEALTH_SUMMARY_TYPE
      );
      setInsight(pickLatest(res?.data ?? []));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { insight, loading, error, refetch: fetchSummary };
}
